import type { DoorCatalogue } from '../data/doorCatalogue';
import type { HandleProduct } from '../doors/drilling';
import { firstColour, offers, rangesFor } from '../doors/orderState';
import {
  defaultDoors,
  newCabinet,
  refit,
  standardDrawerFronts,
  standardShelves,
  withType,
  type BayKind,
  type Build,
  type Cabinet,
  type CabinetType,
  type CornerKind,
} from './model';

/**
 * The cabinet builder's state: an ORDER of cabinets (B8.1), one of them open in
 * the editor, and the door range and colour all their fronts share.
 */
export interface CabinetOrderState {
  rangeId: string;
  colourId: string;
  /** The colour to paint when `colourId` is `ral`, as the shopper typed it. */
  paintColour: string;
  handle: HandleProduct;
  active: number;
  cabinets: Cabinet[];
}

export type SizeField = 'w' | 'b' | 'h' | 'h2' | 'd' | 'blank' | 'upper';

export type CabinetOrderAction =
  | { type: 'range'; rangeId: string }
  | { type: 'colour'; colourId: string }
  | { type: 'paintColour'; value: string }
  | { type: 'handleProduct'; handle: HandleProduct }
  | { type: 'select'; index: number }
  | { type: 'add'; cabinetType?: CabinetType }
  | { type: 'duplicate'; index: number }
  | { type: 'remove'; index: number }
  | { type: 'cabinetType'; cabinetType: CabinetType }
  | { type: 'corner'; corner: CornerKind }
  | { type: 'blind'; side: 'left' | 'right' }
  | { type: 'size'; field: SizeField; value: number }
  | { type: 'qty'; value: number }
  | { type: 'doors'; count: number }
  | { type: 'hinge'; side: 'left' | 'right' }
  | { type: 'fronts'; on: boolean }
  | { type: 'build'; build: Build }
  | { type: 'shelfCount'; count: number }
  | { type: 'shelf'; at: number; value: number }
  | { type: 'shelvesReset' }
  | { type: 'drawerCount'; count: number }
  | { type: 'drawerFront'; at: number; value: number }
  | { type: 'drawersReset' }
  | { type: 'bayAdd' }
  | { type: 'bayRemove'; at: number }
  | { type: 'bayKind'; at: number; kind: BayKind }
  | { type: 'bayWidth'; at: number; value: number }
  | { type: 'bayDrawers'; at: number; count: number }
  | { type: 'baysShare' }
  /** A whole order put back, e.g. one kept across a trip to sign in. */
  | { type: 'restore'; order: CabinetOrderState };

const whole = (value: number) => (Number.isFinite(value) ? Math.round(value) : 0);
const clampInt = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, whole(value)));

export interface CabinetPreset {
  rangeId?: string;
  colourId?: string;
  cabinetType?: CabinetType;
  corner?: CornerKind;
  /** False opens on a carcass-only cabinet (`?fronts=none`). */
  fronts?: boolean;
}

export function initialCabinetOrder(catalogue: DoorCatalogue | undefined, preset: CabinetPreset = {}): CabinetOrderState {
  const ranges = catalogue ? rangesFor(catalogue, 'kitchen') : [];
  const range = ranges.find((r) => r.id === preset.rangeId) ?? ranges[0];
  const colourId = range ? (offers(range, preset.colourId) ? (preset.colourId as string) : firstColour(range)) : '';
  const cabinet = newCabinet(preset.cabinetType ?? 'base', preset.corner ?? 'l');
  return {
    rangeId: range?.id ?? '',
    colourId,
    paintColour: '',
    handle: range?.noHandle ? 'none' : 'bar',
    active: 0,
    cabinets: [{ ...cabinet, fronts: preset.fronts ?? true }],
  };
}

function withActive(state: CabinetOrderState, change: (c: Cabinet) => Cabinet): CabinetOrderState {
  const current = state.cabinets[state.active];
  if (!current) return state;
  const cabinets = state.cabinets.slice();
  cabinets[state.active] = change(current);
  return { ...state, cabinets };
}

function withBays(c: Cabinet, change: (bays: Cabinet['bays']) => Cabinet['bays']): Cabinet {
  return { ...c, bays: change(c.bays.map((b) => ({ ...b }))) };
}

export function cabinetOrderReducer(catalogue: DoorCatalogue | undefined) {
  return function reduce(state: CabinetOrderState, action: CabinetOrderAction): CabinetOrderState {
    switch (action.type) {
      case 'range': {
        if (!catalogue) return state;
        const range = catalogue.ranges().find((r) => r.id === action.rangeId && !r.wardrobe);
        if (!range) return state;
        const previous = catalogue.ranges().find((r) => r.id === state.rangeId);
        const handle: HandleProduct = range.noHandle ? 'none' : previous?.noHandle ? 'bar' : state.handle;
        const colourId = offers(range, state.colourId) ? state.colourId : firstColour(range);
        return { ...state, rangeId: range.id, colourId, handle };
      }
      case 'colour': {
        const range = catalogue?.ranges().find((r) => r.id === state.rangeId);
        return range && offers(range, action.colourId) ? { ...state, colourId: action.colourId } : state;
      }
      case 'paintColour':
        return { ...state, paintColour: action.value.slice(0, 80) };
      case 'handleProduct': {
        const range = catalogue?.ranges().find((r) => r.id === state.rangeId);
        return range?.noHandle ? state : { ...state, handle: action.handle };
      }
      case 'select':
        return state.cabinets[action.index] ? { ...state, active: action.index } : state;
      case 'add': {
        const last = state.cabinets[state.cabinets.length - 1];
        const fresh = newCabinet(action.cabinetType ?? 'base');
        const cabinet = last ? { ...fresh, fronts: last.fronts, build: last.build } : fresh;
        return { ...state, cabinets: [...state.cabinets, cabinet], active: state.cabinets.length };
      }
      case 'duplicate': {
        const source = state.cabinets[action.index];
        if (!source) return state;
        const copy: Cabinet = JSON.parse(JSON.stringify(source));
        const cabinets = [...state.cabinets.slice(0, action.index + 1), copy, ...state.cabinets.slice(action.index + 1)];
        return { ...state, cabinets, active: action.index + 1 };
      }
      case 'remove': {
        if (state.cabinets.length <= 1 || !state.cabinets[action.index]) return state;
        const cabinets = state.cabinets.filter((_, i) => i !== action.index);
        return { ...state, cabinets, active: Math.min(state.active, cabinets.length - 1) };
      }
      case 'cabinetType':
        return withActive(state, (c) => (c.type === action.cabinetType ? c : withType(c, action.cabinetType)));
      case 'corner':
        return withActive(state, (c) => (c.type === 'corner' && c.corner === action.corner ? c : withType(c, 'corner', action.corner)));
      case 'blind':
        return withActive(state, (c) => ({ ...c, blind: action.side }));
      case 'size':
        return withActive(state, (c) => {
          const next = { ...c, [action.field]: whole(action.value) };
          // Door count follows the width until the shopper picks one.
          if (action.field === 'w' && c.doors === defaultDoors(c)) next.doors = defaultDoors(next);
          return refit(next);
        });
      case 'qty':
        return withActive(state, (c) => ({ ...c, qty: clampInt(action.value, 1, 99) }));
      case 'doors':
        return withActive(state, (c) => ({ ...c, doors: clampInt(action.count, 1, 4) }));
      case 'hinge':
        return withActive(state, (c) => ({ ...c, hinge: action.side }));
      case 'fronts':
        return withActive(state, (c) => ({ ...c, fronts: action.on }));
      case 'build':
        return withActive(state, (c) => ({ ...c, build: action.build }));
      case 'shelfCount':
        return withActive(state, (c) => ({ ...c, shelves: standardShelves(c, clampInt(action.count, 0, 8)), shelvesCustom: false }));
      case 'shelf':
        return withActive(state, (c) => {
          if (c.shelves[action.at] === undefined) return c;
          const shelves = c.shelves.slice();
          shelves[action.at] = whole(action.value);
          return { ...c, shelves, shelvesCustom: true };
        });
      case 'shelvesReset':
        return withActive(state, (c) => ({ ...c, shelves: standardShelves(c, c.shelves.length), shelvesCustom: false }));
      case 'drawerCount':
        return withActive(state, (c) => ({ ...c, drawerFronts: standardDrawerFronts(c, clampInt(action.count, 1, 5)), drawersCustom: false }));
      case 'drawerFront':
        return withActive(state, (c) => {
          if (c.drawerFronts[action.at] === undefined) return c;
          const drawerFronts = c.drawerFronts.slice();
          drawerFronts[action.at] = whole(action.value);
          return { ...c, drawerFronts, drawersCustom: true };
        });
      case 'drawersReset':
        return withActive(state, (c) => ({ ...c, drawerFronts: standardDrawerFronts(c, c.drawerFronts.length), drawersCustom: false }));
      case 'bayAdd':
        return withActive(state, (c) => {
          const left = c.w - c.bays.reduce((s, b) => s + b.w, 0);
          if (left < 150) return c;
          return withBays(c, (bays) => [...bays, { kind: 'pullout', w: Math.min(600, left) }]);
        });
      case 'bayRemove':
        return withActive(state, (c) => (c.bays.length <= 1 ? c : withBays(c, (bays) => bays.filter((_, i) => i !== action.at))));
      case 'bayKind':
        return withActive(state, (c) =>
          withBays(c, (bays) => {
            const bay = bays[action.at];
            if (bay) {
              bay.kind = action.kind;
              if (action.kind === 'drawers') bay.n = bay.n ?? 3;
              else delete bay.n;
            }
            return bays;
          }),
        );
      case 'bayWidth':
        return withActive(state, (c) =>
          withBays(c, (bays) => {
            const bay = bays[action.at];
            if (bay) bay.w = whole(action.value);
            return bays;
          }),
        );
      case 'bayDrawers':
        return withActive(state, (c) =>
          withBays(c, (bays) => {
            const bay = bays[action.at];
            if (bay && bay.kind === 'drawers') bay.n = clampInt(action.count, 1, 5);
            return bays;
          }),
        );
      case 'baysShare':
        return withActive(state, (c) =>
          withBays(c, (bays) => {
            const each = Math.floor(c.w / bays.length);
            return bays.map((b, i) => ({ ...b, w: i === bays.length - 1 ? c.w - each * (bays.length - 1) : each }));
          }),
        );
      case 'restore': {
        const cabinets = action.order.cabinets.filter(Boolean);
        if (!cabinets.length) return state;
        // An order kept before paint-to-order existed has no colour to paint.
        return { ...action.order, paintColour: action.order.paintColour ?? '', cabinets, active: clampInt(action.order.active, 0, cabinets.length - 1) };
      }
      default:
        return state;
    }
  };
}
